/**
 * Pure per-render filter for the dashboard nav config. The config module is a
 * shared singleton, so this must never mutate it (a destructive filter here
 * permanently deleted entries — e.g. the Nimi invoices link stayed hidden after
 * enabling the provider until a full page reload).
 *
 * ctx: { user, marketplace, primaryAccount, userAllowToAccess, isAdmin,
 *        isLimitedRole, routeParams }
 * `isAdmin` is the precomputed ADMIN check so this file stays free of the
 * RoleGuard JSX import and node-testable.
 */
export function filterNavConfig(navConfig, ctx) {
    const { user, marketplace, primaryAccount, userAllowToAccess, isAdmin, isLimitedRole, routeParams = {} } = ctx;

    const shouldRenderItem = (item) => {
        if (item.isSellerRoute) {
            if (isLimitedRole) return false;
            return user?.isSeller;
        }
        if (item.isCashierRoute) {
            return primaryAccount?.hasCashier && userAllowToAccess(item.minimumRoleAllowedAccess);
        }
        if (item.isCalendarRoute) {
            if (isLimitedRole) return false;
            return marketplace?.hasCalendar;
        }
        if (item.isSellersManagerRoute) {
            return marketplace?.isSellersManager;
        }
        if (item.isCrmRoute) {
            return marketplace?.hasCRM;
        }
        if (item.isMarketPlaceOwnerRoute) {
            return user?.isMarketPlaceOwner;
        }
        if (item.isAllowSeasonTicketRoute) {
            return marketplace?.isAllowSeasonTicket;
        }
        if (item.isPromoterRoute) {
            return user?.isPromoter;
        }
        if (item.minimumRoleAllowedAccess) {
            return userAllowToAccess(item.minimumRoleAllowedAccess);
        }
        return true;
    };

    const shouldRenderChild = (childRoute) => {
        if (childRoute.isAllowSeasonTicketRoute && !marketplace?.isAllowSeasonTicket) return false;
        if (childRoute.isCrmRoute && !marketplace?.hasCRM) return false;
        if (childRoute.isSellerRoute && !user?.isSeller) return false;
        // mirror the route's ADMIN guard so lower roles don't see a dead link
        if (childRoute.isNimiInvoiceRoute && !(primaryAccount?.invoiceProvider === 'nimi' && isAdmin)) return false;
        if (childRoute.isSellersManagerRoute && !marketplace?.isSellersManager) return false;
        if (childRoute.isMarketPlaceOwnerRoute && !user?.isMarketPlaceOwner) return false;
        return true;
    };

    return navConfig
        .filter((navItem) => !navItem.dependOn || Object.keys(routeParams).includes(navItem.dependOn))
        .map((navItem) => {
            if (!navItem.items?.length) return navItem;
            const items = navItem.items
                .filter(shouldRenderItem)
                .map((item) =>
                    item.children?.length
                        ? { ...item, children: item.children.filter(shouldRenderChild) }
                        : item
                );
            return { ...navItem, items };
        });
}
